import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Play, BookOpen, ArrowRight, Phone, Clock, Calendar, Star, Users, TrendingUp } from 'lucide-react';
import { OrganizationSchema, PersonSchema, WebSiteSchema, PageMeta, SpeakableSchema, FAQSchema, LocalBusinessSchema, AggregateRatingSchema } from '../components/SchemaMarkup';
import { trackBookACallClick } from '../components/Analytics';
import { usePageEditor } from '../components/PageEditorContext';
import EditableSection from '../components/EditableSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = "/katies-plan";
// Use local image to avoid Cloudflare blocking
const KATIE_IMAGE = "/katie-bell.webp";
const PODCAST_ARTWORK = "https://storage.buzzsprout.com/iz7opxekqrpimj3axlopllx63bij?.jpg";

// Animated Card component with scroll trigger
const AnimatedPainCard = ({ pain, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 500);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div 
      ref={cardRef}
      className={`group flex items-start gap-4 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-out ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
      }`}
      data-testid={`pain-card-${index + 1}`}
    >
      <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#E91E8C] to-[#1B2B4B] text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg shadow-[#E91E8C]/30 group-hover:scale-110 transition-transform">
        {index + 1}
      </div>
      <div>
        <p className="font-bold text-gray-900 mb-1">{pain.title}</p>
        <p className="text-gray-600 text-sm">{pain.text}</p>
      </div>
    </div>
  );
};

// Pain points - "Does This Sound Familiar?"
const PAIN_POINTS = [
  { title: "It All Comes Back to You", text: "Your diary's full — your team's isn't. You've explained what needs to happen more times than you can count, and somehow it still comes back to you." },
  { title: "Turnover Up, Take-Home Flat", text: "The numbers go up, but your take-home doesn't follow. You couldn't say exactly where the money goes each month, only that there's rarely enough left over." },
  { title: "No Real Time Off", text: "You can't remember your last proper holiday. If something breaks, if someone's off sick, if a patient's unhappy — it lands on your desk." },
  { title: "The Pressure Never Lifts", text: "You've tried the marketing, the pricing tweaks, another pair of hands. The pressure never quite lifts." }
];

// Client quotes - Proof section
const CLIENT_QUOTES = [
  {
    quote: "Before Thrive, we were overwhelmed and exhausted. Revenue was poor, we had no structure and no plan. Since joining, we've increased our prices, built a great team, implemented strong systems and significantly increased revenue — while reducing our clinical hours to just two days a week. For the first time, we truly feel in control of the business and our lives.",
    name: "Rob and Vicki Kelly, Grosvenor Street Physiotherapy",
    stat: "2 days",
    statLabel: "a week in clinic — with revenue up"
  },
  {
    quote: "Working with Thrive has completely changed the trajectory of our clinic. In the last two years our revenue has nearly tripled, and my personal income is higher than ever. I've gone from feeling burnt out and overwhelmed to having far more time away from the business while it continues to grow.",
    name: "Jenny Clarke, Saxilby Physiotherapy & Wellbeing",
    stat: "3x",
    statLabel: "revenue growth in two years"
  },
  {
    quote: "From £16k profit to £23k in 8 months. I'm working 20 hours in the business rather than 40. I have my life back.",
    name: "Kelly, Body Fit Solutions",
    stat: "+44%",
    statLabel: "profit in 8 months — on half the hours"
  }
];

// Video testimonials
const VIDEO_TESTIMONIALS = [
  {
    name: "Vicky Lewis-Baldy",
    clinic: "Hybrid Health Physiotherapy",
    videoUrl: "/videos/home-vicky-lewis-baldy.mp4",
    thumbnailUrl: "/videos/home-vicky-lewis-baldy-thumb.jpg"
  },
  {
    name: "Helen Reed",
    clinic: "Physio Plus",
    videoUrl: "/videos/home-helen-reed.mp4",
    thumbnailUrl: "/videos/home-helen-reed-thumb.jpg"
  },
  {
    name: "Vicky Baker",
    clinic: "Select Physio",
    videoUrl: "/videos/home-vicky-baker.mp4",
    thumbnailUrl: "/videos/home-vicky-baker-thumb.jpg"
  },
  {
    name: "Beth Sykes",
    clinic: "Rural Rehab",
    videoUrl: "/videos/home-beth-sykes.mp4",
    thumbnailUrl: "/videos/home-beth-sykes-thumb.jpg"
  }
];

// FAQs for SEO - Business-focused questions clinic owners search for
const HOME_FAQS = [
  {
    question: "How do I save TYB Podcast to my phone's home screen?",
    answer: "You can install TYB Podcast as an app on your phone in under 30 seconds! On iPhone/iPad: Open Safari, tap the Share button (⬆️) at the bottom, scroll down and tap 'Add to Home Screen', then tap 'Add'. On Android: Open Chrome, tap the 3-dot menu (⋮) in the top-right, tap 'Install app' or 'Add to Home screen', then confirm. The app icon will appear on your home screen and opens full-screen just like any other app.",
    link: "/",
    linkText: "You're already here - follow the steps above!"
  },
  {
    question: "How do I grow my physiotherapy clinic without working more hours?",
    answer: "Growing your clinic without burning out requires systemising your business, building a team, and focusing on high-value activities. Katie Bell's podcast covers strategies like pricing for profit, hiring the right team members, and creating systems that allow your clinic to run without you being there every hour. Listen to the 'Systems & Processes' episodes for actionable steps.",
    link: "/topics/systems-processes",
    linkText: "Explore Systems & Processes episodes"
  },
  {
    question: "What should I charge for physiotherapy sessions in the UK?",
    answer: "Pricing is one of the biggest challenges for clinic owners. Many physios undercharge due to fear of losing patients. The key is understanding your value, your costs, and your market. Episodes on pricing strategy cover how to calculate your true hourly rate, position premium services, and communicate value to patients.",
    link: "/topics/pricing-strategy",
    linkText: "Listen to Pricing Strategy episodes"
  },
  {
    question: "How do I get more patients for my private physiotherapy clinic?",
    answer: "Patient acquisition combines online presence, referral systems, and local marketing. Successful clinics focus on Google reviews, SEO, social media content, and building relationships with GPs and consultants. Our marketing episodes cover everything from Facebook ads to building a referral machine.",
    link: "/topics/marketing",
    linkText: "Browse Marketing episodes"
  },
  {
    question: "Should I hire an associate physiotherapist for my clinic?",
    answer: "Hiring is a critical growth step but timing matters. You should consider hiring when you're consistently fully booked 4+ weeks ahead and turning away patients. The right hire can double your capacity while freeing you for business development. Learn the hiring process, contracts, and onboarding in our team building episodes.",
    link: "/topics/team-building",
    linkText: "Watch Team Building content"
  },
  {
    question: "How do I stop feeling overwhelmed running my own clinic?",
    answer: "Overwhelm usually comes from trying to do everything yourself without systems or support. Start by identifying your biggest time drains, then either systematise, delegate, or eliminate them. Katie's mindset episodes address the mental side of business ownership including imposter syndrome, boundaries, and sustainable growth.",
    link: "/topics/mindset",
    linkText: "Explore Mindset episodes"
  },
  {
    question: "What business coaching is available for UK physio clinic owners?",
    answer: "Thrive Business Coaching, founded by Katie Bell, specifically supports UK physiotherapists, osteopaths, sports therapists, podiatrists and clinic owners. The programme includes 1:1 coaching, group masterminds, and proven systems for clinic growth. Apply for Katie's Plan for Your Clinic to see if it's right for you.",
    link: BOOKING_URL,
    linkText: "Apply for Katie's Plan",
    external: true
  },
  {
    question: "How much revenue should a physiotherapy clinic make?",
    answer: "Revenue varies hugely based on location, services, and business model. A solo practitioner might generate £60-100k while a multi-practitioner clinic can exceed £500k+. More important than revenue is profit margin - many clinics turn over significant sums but the owner takes home very little. Our finance episodes break down the numbers.",
    link: "/topics/finance",
    linkText: "Listen to Finance episodes"
  },
  {
    question: "How do I create a business plan for my physiotherapy clinic?",
    answer: "A clinic business plan should cover your vision, target market, services, pricing, marketing strategy, financial projections, and growth plans. Start with where you want to be in 3-5 years, then work backwards. The 'Start Here' episodes on this site walk you through creating a plan that actually works.",
    link: "/topics/start-here",
    linkText: "Start with the basics"
  }
];

// Animated pillar card for What Changes section
const PILLARS = [
  { title: "Know Your Numbers", text: "Knowing your numbers properly, not just glancing at your bank balance." },
  { title: "Consistent Clients", text: "A consistent flow of clients, not just word of mouth." },
  { title: "A Team You Trust", text: "A team you can actually rely on, not just one you're constantly chasing." },
  { title: "CEO, Not Just Clinician", text: "Stepping into the role of CEO, not just clinician." }
];

const AnimatedPillar = ({ pillar, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 300);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className={`group flex items-start gap-4 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-out ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
      }`}
      data-testid={`pillar-card-${index + 1}`}
    >
      <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#E91E8C] to-[#1B2B4B] text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg shadow-[#E91E8C]/30 group-hover:scale-110 transition-transform">
        {index + 1}
      </div>
      <div>
        <p className="font-bold text-gray-900 mb-1">{pillar.title}</p>
        <p className="text-gray-600 text-sm">{pillar.text}</p>
      </div>
    </div>
  );
};

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { loadPageContent, isEditMode } = usePageEditor();

  useEffect(() => {
    fetchHomeData();
    loadPageContent('homepage');
  }, [loadPageContent]);

  const fetchHomeData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/home`);
      const data = await response.json();
      setHomeData(data);
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E91E8C]"></div>
      </div>
    );
  }

  return (
    <div>
      <PageMeta 
        title="Specialist Business Coaching for UK Clinic Owners | Thrive"
        description="Specialist business coaching for UK clinic owners. Build a stronger team, improve profitability and create a clinic that grows without depending on you."
        url="https://thrive-businesscoaching.com"
      />
      <OrganizationSchema />
      <PersonSchema />
      <WebSiteSchema />
      <LocalBusinessSchema />
      <AggregateRatingSchema rating="5" reviewCount="500" itemName="Thrive Business Coaching for UK Clinic Owners" />
      <SpeakableSchema 
        url="https://thrive-businesscoaching.com"
        headline="From the outside, everything looks good. So why doesn't it feel like it?"
        summary="Thrive helps physiotherapy, osteopathy, sports therapy and podiatry clinic owners build a clinic that pays them properly and doesn't need them in every decision."
      />
      <FAQSchema faqs={HOME_FAQS} />
      
      {/* 1. Hero Section with Katie's Photo and Ken Burns Effect */}
      <section className={`relative bg-[#1B2B4B] text-white overflow-hidden min-h-[85vh] flex items-center pb-20 ${isEditMode ? 'mt-0' : ''}`}>
        {/* Ken Burns Background Image */}
        <div className="absolute inset-0 ken-burns-container">
          <img 
            src={KATIE_IMAGE}
            alt="Katie Bell - Business Coach for UK Clinic Owners, Physiotherapy Business Owner Coach"
            fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover object-top ken-burns-image opacity-50"
          />
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2B4B]/90 via-[#1B2B4B]/55 to-[#1B2B4B]/35" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <EditableSection sectionId="hero">
                <div className="inline-flex items-center gap-2 bg-[#E91E8C]/20 border border-[#E91E8C]/30 text-[#E91E8C] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Star className="w-4 h-4" />
                  <span>Business Coaching for Physiotherapy, Osteopathy, Sports Therapy & Podiatry Clinic Owners</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white speakable-headline">
                  <span className="block">Your clinic is growing.</span>
                  <span className="block text-[#E91E8C]">So why are you still carrying so much of it?</span>
                </h1>
                
                <p className="text-lg text-gray-300 mb-4 leading-relaxed">
                  Thrive provides specialist business coaching for UK clinic owners, including physiotherapy, osteopathy, sports therapy and podiatry practices.
                </p>
                <p className="text-xl text-gray-300 mb-4 leading-relaxed speakable-summary">
                  You're fully booked. Your team needs you. Decisions are all on you! And somehow, you're still the worst-paid person in the business.
                </p>
                <p className="text-xl text-white font-semibold mb-8 leading-relaxed">
                  You've built a great clinic. Now it's time to build a great business.
                </p>
              </EditableSection>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={BOOKING_URL}
                                    onClick={() => trackBookACallClick('home_hero')}
                  className="inline-flex items-center justify-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-[#E91E8C]/30 book-call-cta"
                  data-testid="hero-book-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Apply for Katie's Plan
                </a>
                <Link
                  to="/listen"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors border border-white/20"
                  data-testid="listen-podcast-btn"
                >
                  <Headphones className="w-5 h-5" />
                  Listen to the Podcast
                </Link>
              </div>
            </div>
            
            {/* Right - Katie's Photo Card with Bullet Points */}
            <div className="block">
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-72 h-72 bg-[#E91E8C]/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-[#00B4D8]/20 rounded-full blur-3xl" />
                
                {/* Photo Card */}
                <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-4 sm:p-6 border border-white/20 shadow-2xl">
                  <img 
                    src="/images/katie-clinic-outside.jpg"
                    alt="Katie Bell outside her physiotherapy and wellness clinic"
                    className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-2xl mb-4"
                    data-testid="katie-hero-image"
                  />
                  <EditableSection sectionId="hero-card">
                    <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-white mb-1">Founded and Led by Katie Bell</h2>
                    </div>
                    <ul className="text-gray-300 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#E91E8C] mt-0.5">✓</span>
                        <span>Physiotherapist by trade</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#E91E8C] mt-0.5">✓</span>
                        <span>Clinic owner since 2014</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#E91E8C] mt-0.5">✓</span>
                        <span>Author of Amazon best-selling book Thriving</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#E91E8C] mt-0.5">✓</span>
                        <span>Host of the Treat Your Business podcast</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#E91E8C] mt-0.5">✓</span>
                        <span>Trusted by 500+ UK clinic owners</span>
                      </li>
                    </ul>
                  </EditableSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Does This Sound Familiar? */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="pain-points-heading" className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Does This Sound Familiar?</h2>
            <p className="text-lg text-gray-600 mb-2">
              <span className="block">Patients keep coming back. Your reputation is building.</span>
              <span className="block whitespace-nowrap">From the outside, it all looks like it's working.</span>
            </p>
            <p className="text-lg text-gray-900 font-semibold">
              But you're still the one holding it together.
            </p>
          </EditableSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {PAIN_POINTS.map((pain, index) => (
              <AnimatedPainCard key={index} pain={pain} index={index} />
            ))}
          </div>
          
          <div className="text-center mt-10">
            <EditableSection sectionId="pain-points-message">
              <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
                <span className="block">If that's where you are, you're not doing anything wrong.</span>
                <span className="block text-[#E91E8C] font-semibold whitespace-nowrap">You've just outgrown the way the business currently runs.</span>
              </p>
            </EditableSection>
            <a
              href={BOOKING_URL}
                            onClick={() => trackBookACallClick('home_pain_points')}
              className="inline-flex items-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-6 py-3 rounded-xl font-semibold transition-colors cta-shimmer"
              data-testid="pain-points-cta"
            >
              Apply for Katie's Plan
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* 3. Here's What's Actually Going On */}
      <section className="py-16 bg-[#1B2B4B] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="whats-going-on">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Here's What's Actually Going On</h2>
            <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
              <p>
                Most clinic owners come to us thinking they have a marketing problem, a staffing problem, or a pricing problem. They're searching for the next tactic that will finally fix it.
              </p>
              <p className="text-white font-semibold">
                Almost never is that the real issue.
              </p>
              <p>
                The real issue is usually that you can't see clearly where your money goes, your team is under-utilised while you're overstretched, and the whole business still runs through you — because no one ever set it up to run any other way.
              </p>
              <p className="text-[#E91E8C] font-semibold text-xl text-center pt-2">
                <span className="block">That's not a you problem. It's a business structure problem.</span>
                <span className="block mt-1">And it's fixable.</span>
              </p>
            </div>
          </EditableSection>
        </div>
      </section>

      {/* 4. Why Thrive */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-48 h-48 bg-[#E91E8C]/10 rounded-full blur-3xl" />
              <img 
                src="/images/event-coaching.jpg"
                alt="Katie Bell coaching clinic owners at a Thrive strategy day"
                className="relative rounded-2xl shadow-xl w-full"
              />
            </div>
            
            {/* Content Side */}
            <div>
              <EditableSection sectionId="why-thrive">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Why Thrive
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    We're not generic business consultants who've read about clinics. Thrive was built <strong>by clinic owners, for clinic owners</strong> — people who understand what it's like to be the clinician, the manager, the marketer and the accountant all before 9am.
                  </p>
                  <p>
                    We've helped over 500 UK clinic owners turn a busy practice into one that actually pays them properly, runs without them firefighting every day, and gives them their evenings and holidays back.
                  </p>
                </div>
              </EditableSection>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about-katie-bell"
                  className="inline-flex items-center justify-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                  data-testid="read-our-story-btn"
                >
                  Read Our Story
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href={BOOKING_URL}
                                    onClick={() => trackBookACallClick('home_about_section')}
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                  data-testid="about-book-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Apply for Katie's Plan
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. What Changes - Four Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Pillars Graphic */}
            <div>
              <img 
                src="/images/four-pillars.png"
                alt="The four foundational pillars of a thriving clinic business"
                className="w-full max-w-lg mx-auto"
                data-testid="four-pillars-graphic"
              />
            </div>
            
            {/* Content */}
            <div>
              <EditableSection sectionId="what-changes">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">What Changes</h2>
                <p className="text-lg text-gray-600 mb-6">
                  This is where it starts: <strong>four foundational pillars.</strong>
                </p>
              </EditableSection>
              <div className="space-y-4 mb-8">
                {PILLARS.map((pillar, index) => (
                  <AnimatedPillar key={index} pillar={pillar} index={index} />
                ))}
              </div>
              <EditableSection sectionId="what-changes-outcomes">
                <div className="space-y-3 text-gray-600">
                  <p className="font-semibold text-gray-900">Get those four in place, and everything else changes.</p>
                  <p>Decisions stop landing only on your desk.</p>
                  <p>Growth gets guided by what the numbers actually show, not gut feel under pressure.</p>
                  <p>You step back from the day-to-day — not because you've stopped caring, but because the business no longer needs you in every decision.</p>
                </div>
              </EditableSection>
              
              <div className="mt-8">
                <Link
                  to="/elevate"
                  className="inline-flex items-center gap-2 text-[#E91E8C] font-semibold text-lg hover:gap-3 transition-all"
                  data-testid="see-how-it-works-btn"
                >
                  See how it works <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Proof - Video Testimonials + Client Quotes */}
      <section className="py-20 bg-[#1B2B4B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="testimonials-heading" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Real Results from Real Clinic Owners</h2>
            <p className="text-gray-400 text-lg">Hear what our members have to say</p>
          </EditableSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VIDEO_TESTIMONIALS.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10"
              >
                <div className="aspect-video bg-black">
                  <video 
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                    poster={testimonial.thumbnailUrl}
                    data-testid={`testimonial-video-${index}`}
                  >
                    <source src={testimonial.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#E91E8C]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#E91E8C] font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.clinic}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Client Quotes */}
          <EditableSection sectionId="proof-quotes" className="mt-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {CLIENT_QUOTES.map((item, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 flex flex-col" data-testid={`client-quote-${index + 1}`}>
                  <div className="border-l-4 border-[#00B4D8] pl-4 mb-5">
                    <p className="text-4xl md:text-5xl font-black text-[#E91E8C] tracking-tighter leading-none">{item.stat}</p>
                    <p className="text-gray-300 text-sm font-semibold uppercase tracking-wider mt-1">{item.statLabel}</p>
                  </div>
                  <div className="flex mb-4">
                    <Star className="h-5 w-5 text-yellow-300 fill-current" />
                    <Star className="h-5 w-5 text-yellow-300 fill-current" />
                    <Star className="h-5 w-5 text-yellow-300 fill-current" />
                    <Star className="h-5 w-5 text-yellow-300 fill-current" />
                    <Star className="h-5 w-5 text-yellow-300 fill-current" />
                  </div>
                  <p className="text-gray-300 italic mb-4 flex-1">"{item.quote}"</p>
                  <p className="text-white font-semibold">— {item.name}</p>
                </div>
              ))}
            </div>
          </EditableSection>

          <div className="text-center mt-12">
            <a
              href={BOOKING_URL}
                            onClick={() => trackBookACallClick('home_testimonials')}
              className="inline-flex items-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 cta-shimmer"
              data-testid="testimonials-cta"
            >
              Start Your Success Story
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#E91E8C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="flex justify-center mb-2">
                <Headphones className="w-8 h-8" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">196+</div>
              <div className="text-white/80 text-sm">Podcast Episodes</div>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Play className="w-8 h-8" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">2,300+</div>
              <div className="text-white/80 text-sm">YouTube Videos</div>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Users className="w-8 h-8" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">500+</div>
              <div className="text-white/80 text-sm">Clinic Owners Helped</div>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <TrendingUp className="w-8 h-8" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold mb-1">10+</div>
              <div className="text-white/80 text-sm">Years Experience</div>
            </div>
            </div>
          </EditableSection>
        </div>
      </section>

      {/* Free Resources - Three Pathways */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="free-resources-heading" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Free Resources</h2>
            <p className="text-gray-600 text-lg">Podcast, videos and articles to help you grow</p>
          </EditableSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Listen */}
            <Link
              to="/listen"
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              data-testid="pathway-listen"
            >
              <div className="aspect-video bg-gradient-to-br from-[#E91E8C] to-[#d11a7d] flex items-center justify-center relative overflow-hidden">
                <img 
                  src={PODCAST_ARTWORK}
                  alt="Treat Your Business Podcast"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <Headphones className="w-8 h-8 text-[#E91E8C]" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Listen</h3>
                <p className="text-gray-600 mb-4">
                  196+ podcast episodes packed with practical advice.
                </p>
                <span className="inline-flex items-center gap-2 text-[#E91E8C] font-semibold group-hover:gap-3 transition-all">
                  Browse episodes <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Watch */}
            <Link
              to="/watch"
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              data-testid="pathway-watch"
            >
              <div className="aspect-video bg-gradient-to-br from-[#00B4D8] to-[#0096c7] flex items-center justify-center relative overflow-hidden">
                <img 
                  src={homeData?.latest_video?.thumbnail_url || "https://i.ytimg.com/vi/rQw--vPhEcw/hqdefault.jpg"}
                  alt="Treat Your Business Videos"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <Play className="w-8 h-8 text-[#00B4D8] ml-1" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Watch</h3>
                <p className="text-gray-600 mb-4">
                  85+ videos on growth, marketing, and building your team.
                </p>
                <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold group-hover:gap-3 transition-all">
                  Watch videos <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Learn */}
            <Link
              to="/learn"
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              data-testid="pathway-learn"
            >
              <div className="aspect-video bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center relative overflow-hidden">
                <img 
                  src="https://i.ytimg.com/vi/36Ok14XooGU/hqdefault.jpg"
                  alt="Treat Your Business Articles"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <BookOpen className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Learn</h3>
                <p className="text-gray-600 mb-4">
                  In-depth articles and guides on growing your clinic.
                </p>
                <span className="inline-flex items-center gap-2 text-purple-600 font-semibold group-hover:gap-3 transition-all">
                  Read articles <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Episode */}
      {homeData?.latest_episode && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Latest Episode</h2>
              <Link to="/listen" className="text-[#E91E8C] font-semibold hover:underline flex items-center gap-1">
                All episodes <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-gradient-to-br from-[#E91E8C]/5 to-[#00B4D8]/5 rounded-2xl p-6 md:p-8 border border-gray-100">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <img 
                    src={homeData.latest_episode.matched_video_thumbnail || homeData.latest_episode.image_url || PODCAST_ARTWORK}
                    alt={homeData.latest_episode.title}
                    className="w-full md:w-48 h-48 object-cover rounded-xl"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(homeData.latest_episode.publish_date)}
                    </span>
                    {homeData.latest_episode.duration && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(homeData.latest_episode.duration)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {homeData.latest_episode.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-2 mb-4">
                    {homeData.latest_episode.description}
                  </p>
                  <Link
                    to={`/podcast/${homeData.latest_episode.slug}`}
                    className="inline-flex items-center gap-2 text-[#E91E8C] font-semibold hover:gap-3 transition-all"
                  >
                    Listen now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section for SEO */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <EditableSection sectionId="faq-heading" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-lg">Common questions from clinic owners - with answers and resources</p>
          </EditableSection>

          <div className="space-y-4" data-testid="faq-section">
            {HOME_FAQS.map((faq, index) => (
              <details 
                key={index}
                className="group bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="font-semibold text-gray-900 text-left pr-4">{faq.question}</h3>
                  <span className="text-[#E91E8C] flex-shrink-0 transition-transform group-open:rotate-45">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p className="mb-4">{faq.answer}</p>
                  {faq.external ? (
                    <a
                      href={faq.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#E91E8C] font-semibold hover:underline"
                    >
                      {faq.linkText} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link
                      to={faq.link}
                      className="inline-flex items-center gap-2 text-[#E91E8C] font-semibold hover:underline"
                    >
                      {faq.linkText} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Community Gallery */}
      <section className="py-20 bg-gray-50 relative overflow-hidden" data-testid="community-gallery-section">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#E91E8C]/10 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <EditableSection sectionId="community-gallery-heading" className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Inside the Thrive Community</h2>
            <p className="text-lg text-gray-600">
              Real clinic owners, real events, real support. This is what it looks like when you stop doing it alone.
            </p>
          </EditableSection>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            <img src="/images/event-workshop-1.jpg" alt="Clinic owners at a Thrive workshop" loading="lazy" className="w-full h-52 md:h-64 object-cover rounded-2xl hover-lift" />
            <img src="/images/event-group.jpg" alt="Thrive community group event" loading="lazy" className="w-full h-52 md:h-64 object-cover rounded-2xl hover-lift" />
            <img src="/images/event-coaching.jpg" alt="Katie Bell coaching clinic owners" loading="lazy" className="w-full h-52 md:h-64 object-cover rounded-2xl hover-lift hidden md:block" />
            <img src="/images/event-strategy-room.jpg" alt="Strategy session at a Thrive event" loading="lazy" className="w-full h-52 md:h-64 object-cover rounded-2xl hover-lift hidden md:block" />
            <div className="bg-[#1B2B4B] rounded-2xl p-6 md:p-8 flex flex-col items-center justify-center text-center h-52 md:h-64 hover-lift col-span-2 md:col-span-1">
              <p className="text-white font-bold text-lg md:text-xl mb-4 leading-snug">Join clinic owners in our free private community</p>
              <a
                href="https://www.facebook.com/groups/thrivebusinesscoaching"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-6 py-3 rounded-full font-semibold transition-colors"
                data-testid="community-gallery-fb-btn"
              >
                Join the Facebook Group <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <img src="/images/event-workshop-2.jpg" alt="Interactive workshop for clinic owners" loading="lazy" className="w-full h-52 md:h-64 object-cover rounded-2xl hover-lift hidden md:block" />
          </div>
        </div>
      </section>

      {/* 7. Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#E91E8C] to-[#d11a7d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <EditableSection sectionId="final-cta">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              You didn't get into this to spend every day firefighting.
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's find out what's actually holding your clinic back.
            </p>
          </EditableSection>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
                            onClick={() => trackBookACallClick('home_final_cta')}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#E91E8C] hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold text-lg transition-colors shadow-lg cta-shimmer"
              data-testid="final-cta-book-call"
            >
              <Phone className="w-5 h-5" />
              Apply for Katie's Plan
            </a>
          </div>
          <EditableSection sectionId="final-cta-note" className="mt-6">
            <p className="text-white/80">
              One conversation, no pressure, just clarity on what's really going on in your business.
            </p>
          </EditableSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
